import React, { useState } from 'react';
import { useCart } from '../CartContext';

function StarRating({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (rating >= i) stars.push('★');
    else if (rating >= i - 0.5) stars.push('½');
    else stars.push('☆');
  }
  return <span style={{ color: '#eab308', fontSize: '0.85rem' }}>{stars.join('')}</span>;
}

export default function ProductCard({ product }) {
  const { addToCart, cartItems } = useCart();
  const [added, setAdded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');

  // Get current quantity of this product in cart
  const getCurrentQuantity = () => {
    const cartItem = cartItems.find(item => item.id === product.id);
    return cartItem ? cartItem.qty : 0;
  };

  const handleAdd = () => {
    const currentQty = getCurrentQuantity();
    const newQty = currentQty + 1;
    
    addToCart({ ...product, isB2B: false });
    setAdded(true);
    
    // Set tooltip message based on quantity
    if (newQty === 1) {
      setTooltipMessage(`✓ ${product.name} added to your cart`);
    } else {
      setTooltipMessage(`✓ ${product.name} (${newQty} items) added to your cart`);
    }
    
    setShowTooltip(true);
    setTimeout(() => {
      setAdded(false);
      setShowTooltip(false);
    }, 2000);
  };

  const currentQuantity = getCurrentQuantity();

  return (
    <div className="card-product" style={{ border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fff', overflow: 'hidden', position: 'relative' }}>
      <div style={{ padding: '1rem' }}>
        {/* Image area */}
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
          {product.badge && (
            <span style={{ position: 'absolute', top: 0, left: 0, padding: '2px 7px', borderRadius: '5px', background: product.badgeColor || '#16a34a', color: 'white', fontSize: '0.75rem', fontWeight: 700, zIndex: 1 }}>
              {product.badge}
            </span>
          )}
          
          {/* Quantity badge - shows how many items in cart */}
          {currentQuantity > 0 && (
            <span style={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              padding: '2px 8px', 
              borderRadius: '20px', 
              background: '#16a34a', 
              color: 'white', 
              fontSize: '0.7rem', 
              fontWeight: 700, 
              zIndex: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {currentQuantity} in cart
            </span>
          )}
          
          <a href="#!">
            <img
              src={product.image}
              alt={product.name}
              style={{ width: '100%', height: 160, objectFit: 'contain', display: 'block', background: '#f9fafb' }}
              onError={e => {
                e.target.onerror = null;
                e.target.src = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=400&fit=crop';
              }}
            />
          </a>
          
          {/* Hover action buttons */}
          <div className="card-product-action" style={{ position: 'absolute', bottom: '10%', display: 'flex', gap: '6px', justifyContent: 'center' }}>
            {[
              // { title: 'Quick View', icon: <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"/><path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"/></> },
              // { title: 'Wishlist', icon: <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572"/></> },
              // { title: 'Compare', icon: <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 10h14l-4 -4"/><path d="M17 14h-14l4 4"/></> },
            ].map(({ title, icon }) => (
              <button key={title} title={title} style={{ width: 34, height: 34, background: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.12)', transition: 'background 0.15s, color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#16a34a'; e.currentTarget.querySelector('svg').style.stroke = 'white'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.querySelector('svg').style.stroke = 'currentColor'; }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <a href="#!" style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '4px' }}>{product.category}</a>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <a href="#!" style={{ color: 'inherit', textDecoration: 'none' }}>{product.name}</a>
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
          <StarRating rating={product.rating} />
          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{product.rating} ({product.reviews})</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.9375rem' }}>₹{product.price}</span>
            {product.originalPrice && (
              <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '0.8125rem', marginLeft: '6px' }}>₹{product.originalPrice}</span>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button
              onClick={handleAdd}
              onMouseEnter={() => {
                if (currentQuantity > 0 && !showTooltip) {
                  setTooltipMessage(`${currentQuantity} × ${product.name} in cart`);
                  setShowTooltip(true);
                  setTimeout(() => setShowTooltip(false), 1500);
                }
              }}
              className={added ? 'cart-added' : ''}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                padding: '6px 12px', 
                background: added ? '#15803d' : '#16a34a', 
                color: 'white', 
                border: 'none', 
                borderRadius: '7px', 
                cursor: 'pointer', 
                fontWeight: 600, 
                fontSize: '0.8125rem', 
                transition: 'background 0.15s',
                transform: added ? 'scale(0.95)' : 'scale(1)'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
              onMouseLeave={e => e.currentTarget.style.background = added ? '#15803d' : '#16a34a'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14"/><path d="M5 12l14 0"/>
              </svg>
              {added ? 'Added!' : 'Add'}
            </button>
            
            {/* Tooltip */}
            {showTooltip && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '10px',
                padding: '8px 12px',
                background: '#1f2937',
                color: 'white',
                fontSize: '0.75rem',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                animation: 'fadeInUp 0.2s ease-out'
              }}>
                {tooltipMessage}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '6px solid #1f2937'
                }} />
              </div>
            )}
          </div>
        </div>
        
        {/* Quantity indicator line */}
        {currentQuantity > 0 && (
          <div style={{ 
            marginTop: '10px', 
            paddingTop: '8px', 
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '0.7rem', color: '#16a34a', fontWeight: 500 }}>
              {currentQuantity} item{currentQuantity !== 1 ? 's' : ''} in cart
            </span>
            <div style={{ 
              width: `${Math.min((currentQuantity / 10) * 100, 100)}%`, 
              height: '3px', 
              background: '#16a34a', 
              borderRadius: '3px',
              maxWidth: '60px'
            }} />
          </div>
        )}
      </div>
      
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          
          .card-product-action {
            opacity: 0;
            transition: opacity 0.2s;
          }
          
          .card-product:hover .card-product-action {
            opacity: 1;
          }
        `}
      </style>
    </div>
  );
}